// src/eden/core/intentRouter.js
// Intent Router (v2)
// - Rule-based classification with safer NAVIGATE handling
// - Page-aware biasing
// - Minimal entity extraction (route + compare windows)
// - Returns: { intent, confidence, route?, compare?, mode?, raw? }

export const INTENTS = {
  PERFORMANCE_EXPLAIN: "PERFORMANCE_EXPLAIN",
  RECOMMEND_OPTIMIZE: "RECOMMEND_OPTIMIZE",
  COMPARE_PERIODS: "COMPARE_PERIODS",
  TROUBLESHOOT: "TROUBLESHOOT",
  NAVIGATE: "NAVIGATE",
  EXPORT_REPORT: "EXPORT_REPORT",
  GENERAL: "GENERAL",
};

const DEFAULT_RESULT = {
  intent: INTENTS.GENERAL,
  confidence: "low", // low|medium|high
};

/**
 * Allowlist routes Eden is allowed to navigate to (client + superadmin union).
 * Keep these stable; avoid injecting arbitrary URLs.
 */
const ROUTES = [
  "/dashboard",

  // hubs
  "/profile-hub",
  "/campaign-management",
  "/genesis-hub",
  "/nexus-hub",

  // analytics hubs
  "/social-analytics",
  "/platform-analytics",
  "/programmatic-analytics",
  "/cinema-analytics",

  // social channels
  "/facebook-analytics",
  "/instagram-analytics",
  "/tiktok-analytics",
  "/youtube-analytics",
  "/x-analytics",
  "/truthsocial-analytics",
  "/rumble-analytics",
  "/google-analytics",

  // omni channels
  "/audio-analytics",
  "/ingame-analytics",
  "/ctv-analytics",
  "/video-analytics",
  "/mobile-analytics",
  "/display-analytics",
  "/dooh-analytics",
  "/email-analytics",

  // sherloc + ai
  "/sherloc",
  "/sherloc-ai", // client hub route
  "/sherloc-reports",
  "/sherloc-ingest",
  "/ai-optimizations",
  "/optimus",
  "/sherloc-media-planner",
  "/sherloc-media-campaigns",

  // platform / account
  "/integrations",
  "/integration-health",
  "/billing",
  "/notifications",
  "/settings",
  "/install-app",

  // auth
  "/login",
  "/forgot-password",
  "/set-password",
];

/**
 * Human phrase → route mapping.
 * Keep synonyms here so voice & text work naturally.
 */
const ROUTE_ALIASES = [
  { route: "/dashboard", keys: ["dashboard", "home", "main", "start"] },

  { route: "/social-analytics", keys: ["social", "social analytics"] },
  { route: "/platform-analytics", keys: ["platform analytics", "platform performance", "unified analytics"] },
  { route: "/programmatic-analytics", keys: ["omni", "programmatic", "programmatic analytics", "omni analytics"] },
  { route: "/cinema-analytics", keys: ["cinema", "cinema analytics"] },

  { route: "/facebook-analytics", keys: ["facebook"] },
  { route: "/instagram-analytics", keys: ["instagram"] },
  { route: "/tiktok-analytics", keys: ["tiktok", "tik tok"] },
  { route: "/youtube-analytics", keys: ["youtube", "you tube"] },
  { route: "/x-analytics", keys: ["x analytics", "twitter", "x"] },
  { route: "/truthsocial-analytics", keys: ["truth social", "truthsocial"] },
  { route: "/rumble-analytics", keys: ["rumble"] },
  { route: "/google-analytics", keys: ["google", "google search", "paid search"] },

  { route: "/audio-analytics", keys: ["audio"] },
  { route: "/ingame-analytics", keys: ["in game", "in-game", "ingame"] },
  { route: "/ctv-analytics", keys: ["ctv", "connected tv"] },
  { route: "/video-analytics", keys: ["video"] },
  { route: "/mobile-analytics", keys: ["mobile"] },
  { route: "/display-analytics", keys: ["display"] },
  { route: "/dooh-analytics", keys: ["dooh", "out of home", "ooh", "digital out of home"] },
  { route: "/email-analytics", keys: ["email", "email analytics"] },

  { route: "/sherloc-reports", keys: ["sherloc reports", "reports", "ai report", "ai reports"] },
  { route: "/ai-optimizations", keys: ["ai optimizations", "optimizations"] },
  { route: "/optimus", keys: ["optimus", "campaign optimization", "optimization console"] },
  { route: "/campaign-management", keys: ["campaign management", "campaigns"] },
  { route: "/integrations", keys: ["integrations", "connections"] },
  { route: "/integration-health", keys: ["integration health", "health"] },

  { route: "/profile-hub", keys: ["account", "profile", "profile hub"] },
  { route: "/settings", keys: ["settings"] },
  { route: "/notifications", keys: ["notifications"] },
  { route: "/billing", keys: ["billing", "payments"] },
  { route: "/install-app", keys: ["install", "install app"] },

  { route: "/genesis-hub", keys: ["genesis", "genesis hub"] },
  { route: "/nexus-hub", keys: ["nexus", "nexus hub"] },
];

function norm(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function includesAny(t, words) {
  const s = norm(t);
  return words.some((w) => s.includes(norm(w)));
}

/**
 * Extract a navigation route from message using allowlist + aliases.
 */
function extractRoute(message) {
  const m = norm(message);
  if (!m) return null;

  // Direct route mention: "go to /social-analytics"
  const direct = ROUTES.find((r) => m.includes(norm(r)));
  if (direct) return direct;

  // Alias match: "open instagram", "take me to omni analytics"
  for (const a of ROUTE_ALIASES) {
    if (a.keys.some((k) => m.includes(norm(k)))) {
      return a.route;
    }
  }

  return null;
}

/**
 * Lightweight period extraction for comparisons.
 * Returns a compare descriptor (not dates) so backend can compute ranges.
 */
function extractCompare(message) {
  const m = norm(message);

  // Explicit phrases
  if (includesAny(m, ["week over week", "wow"])) return { mode: "WOW" };
  if (includesAny(m, ["month over month", "mom"])) return { mode: "MOM" };
  if (includesAny(m, ["year over year", "yoy"])) return { mode: "YOY" };

  // "last 7 days", "last 30 days"
  const lastDays = m.match(/last\s+(\d{1,3})\s+days?/);
  if (lastDays?.[1]) return { mode: "LAST_N_DAYS", n: Number(lastDays[1]) };

  // "last week", "last month"
  if (includesAny(m, ["last week"])) return { mode: "LAST_WEEK" };
  if (includesAny(m, ["last month"])) return { mode: "LAST_MONTH" };

  // "mtd", "qtd", "ytd"
  if (includesAny(m, ["mtd", "month to date"])) return { mode: "MTD" };
  if (includesAny(m, ["qtd", "quarter to date"])) return { mode: "QTD" };
  if (includesAny(m, ["ytd", "year to date"])) return { mode: "YTD" };

  // "compare X vs Y" (generic signal)
  if (includesAny(m, ["compare", "vs", "versus"])) return { mode: "COMPARE_GENERIC" };

  return null;
}

/**
 * Page-aware bias helpers
 */
function isAnalyticsContext(context) {
  const page = norm(context?.page || "");
  const route = norm(context?.route || "");
  return page.includes("analytics") || route.includes("analytics");
}

function isSherlocContext(context) {
  const page = norm(context?.page || "");
  const route = norm(context?.route || "");
  return page.includes("sherloc") || route.includes("sherloc");
}

/**
 * Main inference
 * @returns { intent, confidence, route?, compare? }
 */
export function inferIntent(message, context = {}) {
  const m = String(message || "").trim();
  if (!m) return { ...DEFAULT_RESULT };

  const lower = norm(m);

  // --- NAVIGATION (safest to detect early) ------------------------
  const wantsNav = includesAny(lower, ["go to", "open", "take me", "navigate", "show me", "bring me to"]);
  const route = extractRoute(lower);
  if (wantsNav && route) {
    return {
      intent: INTENTS.NAVIGATE,
      confidence: "high",
      route,
      raw: { reason: "nav_phrase+route" },
    };
  }
  // If nav phrase but no route match, still mark NAVIGATE (UI can ask follow-up)
  if (wantsNav && !route) {
    return {
      intent: INTENTS.NAVIGATE,
      confidence: "medium",
      route: null,
      raw: { reason: "nav_phrase_no_route" },
    };
  }

  // --- EXPORT -----------------------------------------------------
  if (includesAny(lower, ["export", "pdf", "report", "one-sheeter", "one sheeter", "download"])) {
    return {
      intent: INTENTS.EXPORT_REPORT,
      confidence: "high",
      raw: { reason: "export_keywords" },
    };
  }

  // --- TROUBLESHOOT ------------------------------------------------
  if (includesAny(lower, ["error", "broken", "not working", "issue", "bug", "crash", "failed"])) {
    return {
      intent: INTENTS.TROUBLESHOOT,
      confidence: "high",
      raw: { reason: "troubleshoot_keywords" },
    };
  }

  // --- COMPARE -----------------------------------------------------
  const compare = extractCompare(lower);
  if (compare) {
    return {
      intent: INTENTS.COMPARE_PERIODS,
      confidence: compare.mode === "COMPARE_GENERIC" ? "medium" : "high",
      compare,
      raw: { reason: "compare_detected" },
    };
  }

  // --- OPTIMIZE / RECOMMEND ---------------------------------------
  if (
    includesAny(lower, [
      "optimize",
      "improve",
      "recommend",
      "what should we do",
      "next steps",
      "reallocate",
      "shift budget",
      "increase performance",
      "lower cpc",
      "lower cpm",
      "increase ctr",
    ])
  ) {
    return {
      intent: INTENTS.RECOMMEND_OPTIMIZE,
      confidence: isSherlocContext(context) || isAnalyticsContext(context) ? "high" : "medium",
      raw: { reason: "optimize_keywords" },
    };
  }

  // --- EXPLAIN / PERFORMANCE --------------------------------------
  if (includesAny(lower, ["why", "explain", "what happened", "reason", "break down", "walk me through", "summarize"])) {
    return {
      intent: INTENTS.PERFORMANCE_EXPLAIN,
      confidence: isAnalyticsContext(context) ? "high" : "medium",
      raw: { reason: "explain_keywords" },
    };
  }

  // Page bias: analytics pages default to explain
  if (isAnalyticsContext(context)) {
    return {
      intent: INTENTS.PERFORMANCE_EXPLAIN,
      confidence: "medium",
      raw: { reason: "analytics_context_bias" },
    };
  }

  // Sherloc pages: bias toward optimize
  if (isSherlocContext(context)) {
    return {
      intent: INTENTS.RECOMMEND_OPTIMIZE,
      confidence: "medium",
      raw: { reason: "sherloc_context_bias" },
    };
  }

  return { ...DEFAULT_RESULT };
}

/**
 * Helper: validate / clamp a route before actually navigating
 */
export function isAllowedRoute(route) {
  if (!route) return false;
  return ROUTES.includes(route);
}
